import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Bell, BellOff, Zap, Loader2 } from 'lucide-react';
import { enablePush, disablePush, getPushState, isPushSupported } from '../lib/push';

/**
 * Admin-only card: shows current push status for THIS admin device
 * and lets them send a test notification to their own registered devices.
 */
export default function AdminPushTest() {
  const [state, setState] = useState({ supported: true, permission: 'default', subscribed: false, enabled: false });
  const [busy, setBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [subsCount, setSubsCount] = useState(null);

  const refresh = async () => {
    const s = await getPushState();
    setState(s);
    try {
      const { data } = await api.get('/push/status');
      setSubsCount(data.subscriptions ?? 0);
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    if (!isPushSupported()) {
      toast.error("Notifiche non supportate su questo browser. Su iPhone installa la PWA dalla schermata Home.");
      return;
    }
    setBusy(true);
    try {
      const active = state.subscribed && state.enabled;
      if (active) {
        await disablePush();
        toast.success('Notifiche disattivate');
      } else {
        const res = await enablePush();
        if (res.ok) {
          await api.put('/push/settings', { enabled: true });
          toast.success('Notifiche attivate');
        } else if (res.reason === 'denied') {
          toast.error('Permesso negato dal browser/OS');
        } else if (res.reason === 'subscribe-failed') {
          toast.error('Errore iOS/Safari: ' + (res.error || 'sottoscrizione fallita'));
        } else if (res.reason === 'server-save-failed') {
          toast.error('Server non raggiungibile: ' + (res.error || 'riprova'));
        } else {
          toast.error('Impossibile attivare' + (res.error ? `: ${res.error}` : ''));
        }
      }
      await refresh();
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTestBusy(true);
    try {
      const { data } = await api.post('/push/test');
      if (!data.subscriptions) {
        toast.error('Nessun dispositivo registrato. Attiva prima le notifiche.');
      } else if (data.sent === 0) {
        toast.error(`Invio fallito su ${data.subscriptions} dispositivo/i (potrebbero essere scaduti)`);
      } else {
        toast.success(`Notifica di prova inviata a ${data.sent} dispositivo${data.sent > 1 ? 'i' : ''}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio test');
    } finally {
      setTestBusy(false);
    }
  };

  const active = state.subscribed && state.enabled;
  const blocked = state.permission === 'denied';

  return (
    <div className="space-y-2" data-testid="admin-push-test">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Notifiche push</div>

      <div className="card-surface p-4 flex items-center gap-4">
        {active ? <Bell className="w-5 h-5 text-[var(--gold)]" /> : <BellOff className="w-5 h-5 text-white/50" />}
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-medium flex items-center gap-2">
            {active ? 'Notifiche attive' : 'Notifiche disattivate'}
            {subsCount != null && (
              <span className="text-[10px] text-white/60 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded-full">
                {subsCount} dispositiv{subsCount === 1 ? 'o' : 'i'}
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/50 leading-snug">
            {!state.supported
              ? 'Non supportate su questo browser'
              : blocked
                ? 'Bloccate dal sistema — sblocca dai permessi'
                : active
                  ? 'Ricevi push per messaggi, novità e broadcast'
                  : 'Tocca per attivarle su questo dispositivo'}
          </div>
        </div>
        <button
          data-testid="admin-push-toggle"
          onClick={toggle}
          disabled={busy}
          aria-label={active ? 'Disattiva notifiche' : 'Attiva notifiche'}
          className={`w-11 h-6 rounded-full relative transition-colors ${active ? 'bg-[var(--gold)]' : 'bg-white/15'} disabled:opacity-50`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${active ? 'translate-x-5' : ''}`} />
        </button>
      </div>

      <button
        data-testid="admin-push-test-btn"
        onClick={sendTest}
        disabled={testBusy}
        className="card-surface p-4 w-full flex items-center gap-4 text-left disabled:opacity-60 hover:border-[var(--gold)]/30 transition-colors"
      >
        <div className="w-9 h-9 rounded-full bg-[var(--gold-soft)] border border-[var(--gold)]/30 flex items-center justify-center flex-shrink-0">
          {testBusy ? <Loader2 className="w-4 h-4 animate-spin text-[var(--gold)]" /> : <Zap className="w-4 h-4 text-[var(--gold)]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-medium">Invia notifica di prova</div>
          <div className="text-[11px] text-white/50 leading-snug">
            {testBusy ? 'Invio in corso…' : 'Verifica in 2 secondi che le push arrivino sul tuo dispositivo'}
          </div>
        </div>
      </button>
    </div>
  );
}
